import { ClipboardList, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProjectCostCodes } from "@/hooks/use-budgets";
import { useChangeOrdersForProject } from "@/hooks/use-change-orders";
import { useCommitments } from "@/hooks/use-commitments";
import { authClient } from "@/lib/auth-client";
import { formatCurrency } from "@/lib/formatters";
import { NewPcoDialog } from "./NewPcoDialog";
import { PcoDetailDialog } from "./PcoDetailDialog";

const CO_WRITE_USER_TYPES = new Set(["admin", "executive", "project_manager", "finance"]);
const PCO_STATUSES = ["draft", "pending_approval"] as const;

function PcoStatusBadge({ status }: { status: string }) {
  if (status === "pending_approval") {
    return (
      <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
        Pending Approval
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
      Draft
    </span>
  );
}

export function PcoTab({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, error, refetch } = useChangeOrdersForProject(
    projectId,
    PCO_STATUSES,
  );
  const { data: commitments } = useCommitments({ projectId });
  const { data: costCodes } = useProjectCostCodes(projectId);
  const { data: sessionData } = authClient.useSession();
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const user = sessionData?.user as { role?: string; userType?: string | null } | undefined;
  const canCreate = user?.userType
    ? CO_WRITE_USER_TYPES.has(user.userType)
    : user?.role === "admin" || user?.role === "project_manager";

  const parentLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of commitments ?? []) {
      if (c.id && c.commitmentNumber)
        m.set(c.id, `${c.commitmentNumber} — ${c.title ?? ""}`.trim());
    }
    return m;
  }, [commitments]);

  const costCodeLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const cc of costCodes ?? []) {
      if (cc.id) m.set(cc.id, `${cc.code}${cc.description ? ` — ${cc.description}` : ""}`);
    }
    return m;
  }, [costCodes]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const items = data ?? [];
  const totalPendingValue = items.reduce((s, c) => s + Number(c.originalAmount ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-gray-500">Total Pending PCO Value</p>
          <p className="text-lg font-semibold text-[#334155]">
            {formatCurrency(totalPendingValue, { precise: true })}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setAddOpen(true)}
            className="rounded-lg bg-[#334155] hover:bg-[#415A77] text-white shadow-sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            New PCO
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            No potential change orders in flight for this project.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  PCO #
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Title
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Linkage
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Subcontractor
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                  Amount
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                  Sched Days
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Deadline
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Created
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((co) => (
                <TableRow
                  key={co.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedId(co.id)}
                >
                  <TableCell className="text-sm font-medium text-gray-900">
                    {co.commitmentNumber}
                  </TableCell>
                  <TableCell className="text-sm text-gray-900">{co.title}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {co.pcoLinkageType === "cost_code"
                      ? co.pcoCostCodeId
                        ? (costCodeLookup.get(co.pcoCostCodeId) ?? "Cost code")
                        : "Cost code"
                      : co.parentCommitmentId
                        ? (parentLookup.get(co.parentCommitmentId) ?? "—")
                        : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {co.pcoRequiresBuyout ? (
                      <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        Buyout pending
                      </span>
                    ) : (
                      (co.companyName ?? "—")
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-900 text-right">
                    {formatCurrency(co.originalAmount, { precise: true })}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 text-right">
                    {co.scheduleImpactDays ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {co.clientDecisionDeadline ?? "—"}
                  </TableCell>
                  <TableCell>
                    <PcoStatusBadge status={co.status} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(co.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewPcoDialog projectId={projectId} open={addOpen} onOpenChange={setAddOpen} />
      <PcoDetailDialog
        changeOrderId={selectedId}
        open={selectedId !== null}
        onOpenChange={(o) => {
          if (!o) setSelectedId(null);
        }}
      />
    </div>
  );
}
